'use client';

import React, { useMemo } from 'react';
import { Block, ViewerAnnotation } from './types';

interface MarkdownRendererProps {
  content: string;
  annotations: ViewerAnnotation[];
  onBlockClick?: (blockIndex: number) => void;
}

// Parse markdown into blocks
function parseMarkdown(content: string): Block[] {
  const lines = content.split('\n');
  const blocks: Block[] = [];
  let blockId = 0;
  let inCodeBlock = false;
  let codeBlockContent: string[] = [];
  let codeBlockLanguage = '';

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];

    // Code block handling
    if (line.startsWith('```')) {
      if (inCodeBlock) {
        // End code block
        blocks.push({
          id: `block-${blockId++}`,
          type: 'code',
          content: codeBlockContent.join('\n'),
          language: codeBlockLanguage,
        });
        codeBlockContent = [];
        codeBlockLanguage = '';
        inCodeBlock = false;
      } else {
        // Start code block
        inCodeBlock = true;
        codeBlockLanguage = line.slice(3).trim();
      }
      continue;
    }

    if (inCodeBlock) {
      codeBlockContent.push(line);
      continue;
    }

    // Skip empty lines
    if (!line.trim()) {
      continue;
    }

    // Horizontal rule
    if (/^(-{3,}|\*{3,}|_{3,})$/.test(line.trim())) {
      blocks.push({
        id: `block-${blockId++}`,
        type: 'hr',
        content: '',
      });
      continue;
    }

    // Headings
    const headingMatch = line.match(/^(#{1,6})\s+(.+)$/);
    if (headingMatch) {
      blocks.push({
        id: `block-${blockId++}`,
        type: 'heading',
        content: headingMatch[2],
        level: headingMatch[1].length,
      });
      continue;
    }

    // Blockquote
    if (line.startsWith('>')) {
      blocks.push({
        id: `block-${blockId++}`,
        type: 'blockquote',
        content: line.slice(1).trim(),
      });
      continue;
    }

    // Task list item
    const taskMatch = line.match(/^(\s*)[-*]\s+\[([ xX])\]\s+(.+)$/);
    if (taskMatch) {
      blocks.push({
        id: `block-${blockId++}`,
        type: 'list-item',
        content: taskMatch[3],
        checked: taskMatch[2].toLowerCase() === 'x',
      });
      continue;
    }

    // Unordered list item
    const ulMatch = line.match(/^(\s*)[-*]\s+(.+)$/);
    if (ulMatch) {
      blocks.push({
        id: `block-${blockId++}`,
        type: 'list-item',
        content: ulMatch[2],
      });
      continue;
    }

    // Ordered list item
    const olMatch = line.match(/^(\s*)(\d+)\.\s+(.+)$/);
    if (olMatch) {
      blocks.push({
        id: `block-${blockId++}`,
        type: 'list-item',
        content: olMatch[3],
        ordered: true,
        listIndex: parseInt(olMatch[2], 10),
      });
      continue;
    }

    // Default: paragraph
    blocks.push({
      id: `block-${blockId++}`,
      type: 'paragraph',
      content: line,
    });
  }

  return blocks;
}

// Render inline markdown (bold, italic, code, links)
function InlineMarkdown({ text, annotations, blockIndex }: { text: string; annotations: ViewerAnnotation[]; blockIndex: number }) {
  // Get annotations for this block
  const blockAnnotations = annotations.filter(a => a.position.blockIndex === blockIndex);

  // Sort by start offset
  const sortedAnnotations = [...blockAnnotations].sort((a, b) => a.position.startOffset - b.position.startOffset);

  // Build segments with highlights
  const segments: { text: string; annotation?: ViewerAnnotation }[] = [];
  let lastEnd = 0;

  for (const ann of sortedAnnotations) {
    // Add text before this annotation
    if (ann.position.startOffset > lastEnd) {
      segments.push({ text: text.slice(lastEnd, ann.position.startOffset) });
    }
    // Add annotated text
    segments.push({
      text: text.slice(ann.position.startOffset, ann.position.endOffset),
      annotation: ann,
    });
    lastEnd = ann.position.endOffset;
  }

  // Add remaining text
  if (lastEnd < text.length) {
    segments.push({ text: text.slice(lastEnd) });
  }

  // If no segments, just render the text
  if (segments.length === 0) {
    segments.push({ text });
  }

  return (
    <>
      {segments.map((segment, i) => {
        const processed = processInlineMarkdown(segment.text);

        if (segment.annotation) {
          const highlightClass = getHighlightClass(segment.annotation.type);
          return (
            <mark key={i} className={highlightClass} data-annotation-id={segment.annotation.id}>
              {processed}
            </mark>
          );
        }

        return <span key={i}>{processed}</span>;
      })}
    </>
  );
}

function getHighlightClass(type: ViewerAnnotation['type']): string {
  switch (type) {
    case 'comment':
      return 'bg-yellow-500/20 border-b-2 border-yellow-500/50 px-0.5 rounded-sm';
    case 'highlight':
      return 'bg-primary/20 px-0.5 rounded-sm';
    case 'deletion':
      return 'bg-red-500/20 line-through px-0.5 rounded-sm';
    default:
      return '';
  }
}

function processInlineMarkdown(text: string): React.ReactNode[] {
  const elements: React.ReactNode[] = [];
  let remaining = text;
  let key = 0;

  while (remaining) {
    // Bold: **text** or __text__
    let match = remaining.match(/^(.*?)\*\*(.+?)\*\*/);
    if (!match) match = remaining.match(/^(.*?)__(.+?)__/);
    if (match) {
      if (match[1]) elements.push(<span key={key++}>{match[1]}</span>);
      elements.push(<strong key={key++} className="font-semibold">{match[2]}</strong>);
      remaining = remaining.slice(match[0].length);
      continue;
    }

    // Italic: *text* or _text_
    match = remaining.match(/^(.*?)\*(.+?)\*/);
    if (!match) match = remaining.match(/^(.*?)_(.+?)_/);
    if (match && !match[1].endsWith('*') && !match[1].endsWith('_')) {
      if (match[1]) elements.push(<span key={key++}>{match[1]}</span>);
      elements.push(<em key={key++} className="italic">{match[2]}</em>);
      remaining = remaining.slice(match[0].length);
      continue;
    }

    // Inline code: `code`
    match = remaining.match(/^(.*?)`([^`]+)`/);
    if (match) {
      if (match[1]) elements.push(<span key={key++}>{match[1]}</span>);
      elements.push(
        <code key={key++} className="px-1.5 py-0.5 bg-bg-alt rounded text-sm font-mono text-primary">
          {match[2]}
        </code>
      );
      remaining = remaining.slice(match[0].length);
      continue;
    }

    // Link: [text](url)
    match = remaining.match(/^(.*?)\[([^\]]+)\]\(([^)]+)\)/);
    if (match) {
      if (match[1]) elements.push(<span key={key++}>{match[1]}</span>);
      elements.push(
        <a
          key={key++}
          href={match[3]}
          className="text-primary hover:text-primary-hover underline"
          target="_blank"
          rel="noopener noreferrer"
        >
          {match[2]}
        </a>
      );
      remaining = remaining.slice(match[0].length);
      continue;
    }

    // No more patterns, add remaining text
    elements.push(<span key={key++}>{remaining}</span>);
    break;
  }

  return elements;
}

// Block renderer
function BlockRenderer({ block, index, annotations }: { block: Block; index: number; annotations: ViewerAnnotation[] }) {
  const baseClass = 'block-content select-text';

  switch (block.type) {
    case 'heading': {
      const Tag = `h${block.level}` as keyof React.JSX.IntrinsicElements;
      const sizeClass = {
        1: 'text-2xl font-bold mt-6 mb-4',
        2: 'text-xl font-semibold mt-5 mb-3',
        3: 'text-lg font-semibold mt-4 mb-2',
        4: 'text-base font-semibold mt-3 mb-2',
        5: 'text-sm font-semibold mt-2 mb-1',
        6: 'text-sm font-medium mt-2 mb-1',
      }[block.level || 1];

      return (
        <Tag className={`${baseClass} ${sizeClass} text-text`} data-block-index={index}>
          <InlineMarkdown text={block.content} annotations={annotations} blockIndex={index} />
        </Tag>
      );
    }

    case 'paragraph':
      return (
        <p className={`${baseClass} text-text-secondary leading-relaxed mb-3`} data-block-index={index}>
          <InlineMarkdown text={block.content} annotations={annotations} blockIndex={index} />
        </p>
      );

    case 'blockquote':
      return (
        <blockquote
          className={`${baseClass} border-l-4 border-primary/50 pl-4 py-1 my-3 text-text-muted italic`}
          data-block-index={index}
        >
          <InlineMarkdown text={block.content} annotations={annotations} blockIndex={index} />
        </blockquote>
      );

    case 'list-item': {
      const checkbox = block.checked !== undefined ? (
        <span className={`inline-block w-4 h-4 mr-2 border rounded ${block.checked ? 'bg-primary border-primary' : 'border-border'}`}>
          {block.checked && (
            <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          )}
        </span>
      ) : null;

      const bullet = block.ordered
        ? <span className="text-text-muted mr-2">{block.listIndex}.</span>
        : checkbox || <span className="text-text-muted mr-2">&bull;</span>;

      return (
        <div className={`${baseClass} flex items-start mb-1.5 pl-4`} data-block-index={index}>
          {bullet}
          <span className={block.checked ? 'text-text-muted line-through' : 'text-text-secondary'}>
            <InlineMarkdown text={block.content} annotations={annotations} blockIndex={index} />
          </span>
        </div>
      );
    }

    case 'code':
      return (
        <pre
          className={`${baseClass} bg-bg-alt rounded-lg p-4 my-3 overflow-x-auto font-mono text-sm`}
          data-block-index={index}
        >
          {block.language && (
            <div className="text-xs text-text-muted mb-2 uppercase tracking-wide">{block.language}</div>
          )}
          <code className="text-text-secondary">{block.content}</code>
        </pre>
      );

    case 'hr':
      return <hr className="border-border my-6" data-block-index={index} />;

    default:
      return (
        <p className={`${baseClass} text-text-secondary mb-3`} data-block-index={index}>
          <InlineMarkdown text={block.content} annotations={annotations} blockIndex={index} />
        </p>
      );
  }
}

export function MarkdownRenderer({ content, annotations }: MarkdownRendererProps) {
  const blocks = useMemo(() => parseMarkdown(content), [content]);

  return (
    <div className="markdown-content">
      {blocks.map((block, index) => (
        <BlockRenderer key={block.id} block={block} index={index} annotations={annotations} />
      ))}
    </div>
  );
}
